<template>
  <div id="wrapper">
    <div class="sign-in">
      <router-link :to="{ name: 'login' }">
        <b-button type="submit" variant="outline-primary">Sign in</b-button>
      </router-link>
    </div>
    <div class="sign-up">
      <LogoDescription
        :data="{
          title: 'Create Amejpay Account',
          subtitle: stepSubtitle,
        }"
        :published="true"
      ></LogoDescription>

      <!-- STEP 1 : Country + Mobile Number -->
      <div class="form" v-if="step === 1">
        <form class="form-number">
          <span class="text-danger" v-if="showFormError">{{ formError }}</span>
          <SearchSelect
            v-model="form.country"
            :options="countryOptions"
            value-key="id"
            label-key="name"
            note-key="dial_label"
            :search-keys="['dial_label']"
            :placeholder="countryPlaceholder"
            search-placeholder="Type a country name or code..."
            :has-error="!!fieldError('country')"
          ></SearchSelect>
          <span class="text-danger float-right" v-if="fieldError('country')">{{
            fieldError('country')
          }}</span>

          <input
            type="number"
            v-model.trim="form.phone_number"
            name="phone_number"
            :class="fieldError('phone_number') ? 'errors' : ''"
            required
          />
          <label :alt="phoneLabel" :placeholder="phoneLabel"></label>
          <span class="text-danger" v-if="fieldError('phone_number')">{{
            fieldError('phone_number')
          }}</span>
        </form>
      </div>
      <div class="details" v-if="step === 1">
        <p class="customer-detail">
          By continuing, you confirm that you are
          <br />authorized to use this phone number <br />and agree to receive
          an SMS text. <br />Carrier fees may apply.
        </p>
      </div>

      <!-- STEP 2 : Personal Detail -->
      <div class="basic-detail-form" v-if="step === 2">
        <form class="basic-detail">
          <span class="text-danger" v-if="showFormError">{{ formError }}</span>
          <div class="first-name">
            <input
              type="text"
              v-model.trim="form.first_name"
              :class="fieldError('first_name') ? 'errors' : ''"
              name="first_name"
              required
            />
            <label alt="First Name" placeholder="First Name"></label>
            <span class="text-danger" v-if="fieldError('first_name')">{{
              fieldError('first_name')
            }}</span>
          </div>
          <div class="middle-name">
            <input
              type="text"
              v-model.trim="form.middle_name"
              :class="fieldError('middle_name') ? 'errors' : ''"
              name="middle_name"
              required
            />
            <label alt="Middle Name (Optional)" placeholder="Middle Name (Optional)"></label>
            <span class="text-danger" v-if="fieldError('middle_name')">{{
              fieldError('middle_name')
            }}</span>
          </div>
          <div class="last-name">
            <input
              type="text"
              v-model.trim="form.last_name"
              :class="fieldError('last_name') ? 'errors' : ''"
              name="last_name"
              required
            />
            <label alt="Last Name" placeholder="Last Name"></label>
            <span class="text-danger" v-if="fieldError('last_name')">{{
              fieldError('last_name')
            }}</span>
          </div>
          <div class="date-of-birth">
            <input
              type="date"
              v-model="form.date_of_birth"
              :class="fieldError('date_of_birth') ? 'errors' : ''"
              name="date_of_birth"
              :max="maxDob"
              required
            />
            <label alt="Date of Birth (Optional)" placeholder="Date of Birth (Optional)"></label>
            <span class="text-danger" v-if="fieldError('date_of_birth')">{{
              fieldError('date_of_birth')
            }}</span>
          </div>
        </form>
      </div>

      <!-- STEP 3 : Basic Detail (email + password) -->
      <div class="basic-detail-form" v-if="step === 3">
        <form class="basic-detail">
          <span class="text-danger" v-if="showFormError">{{ formError }}</span>
          <div class="email">
            <input
              type="text"
              v-model.trim="form.email"
              :class="fieldError('email') ? 'errors' : ''"
              name="email"
              required
            />
            <label alt="Email Address" placeholder="Email Address"></label>
            <span class="text-danger" v-if="fieldError('email')">{{
              fieldError('email')
            }}</span>
          </div>
          <div class="password pw-wrap">
            <input
              :type="showPassword ? 'text' : 'password'"
              v-model.trim="form.password"
              :class="fieldError('password') ? 'errors' : ''"
              name="password"
              required
            />
            <!-- After the label on purpose: `input + label` floats it. -->
            <label alt="Password" placeholder="Password"></label>
            <button
              type="button"
              class="pw-toggle"
              :aria-label="showPassword ? 'Hide password' : 'Show password'"
              :title="showPassword ? 'Hide password' : 'Show password'"
              @click="showPassword = !showPassword"
            >
              <span class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</span>
            </button>
            <span class="text-danger" v-if="fieldError('password')">{{
              fieldError('password')
            }}</span>
          </div>
          <div class="confirm pw-wrap">
            <input
              :type="showConfirmPassword ? 'text' : 'password'"
              v-model.trim="form.confirm_password"
              :class="fieldError('confirm_password') ? 'errors' : ''"
              name="confirm_password"
              required
            />
            <label alt="Confirm Password" placeholder="Confirm Password"></label>
            <button
              type="button"
              class="pw-toggle"
              :aria-label="showConfirmPassword ? 'Hide password' : 'Show password'"
              :title="showConfirmPassword ? 'Hide password' : 'Show password'"
              @click="showConfirmPassword = !showConfirmPassword"
            >
              <span class="material-icons">{{ showConfirmPassword ? 'visibility_off' : 'visibility' }}</span>
            </button>
            <span class="text-danger" v-if="fieldError('confirm_password')">{{
              fieldError('confirm_password')
            }}</span>
          </div>
        </form>
      </div>

      <!-- STEP 4 : OTP verification -->
      <div class="basic-detail-form" v-if="step === 4">
        <form class="basic-detail">
          <span class="text-success float-right" v-if="infoMessage">{{ infoMessage }}</span>
          <span class="text-danger" v-if="showFormError">{{ formError }}</span>
          <div class="otp-code">
            <input
              type="text"
              v-model.trim="otp_code"
              :class="fieldError('otp_code') ? 'errors' : ''"
              name="otp_code"
              maxlength="6"
              onkeypress="return event.charCode >= 48 && event.charCode <= 57"
              required
            />
            <label alt="Verification Code" placeholder="Verification Code"></label>
            <span class="text-danger" v-if="fieldError('otp_code')">{{
              fieldError('otp_code')
            }}</span>
          </div>
        </form>
      </div>
      <div class="details" v-if="step === 4">
        <p class="customer-detail">
          We sent a {{ otpLength }}-digit code to
          <br />{{ registerEmail || form.email }}
          <br />It expires in {{ otpExpiresIn }} minutes.
        </p>
      </div>
      <div class="send-code" v-if="step === 4 && !sessionExpired">
        <button
          type="button"
          class="send-code"
          @click="resendCode"
          :disabled="resendCooldown > 0 || isLoading"
        >
          {{ resendCooldown > 0 ? "Send a new code in " + resendCooldown + "s" : "Send a new code" }}
        </button>
      </div>

      <!-- STEP 5 : Success -->
      <div class="details" v-if="step === 5">
        <p class="customer-detail">
          {{ successText }}
          <br />Redirecting you to the sign in page...
        </p>
      </div>

      <!-- NAVIGATION -->
      <div class="next-button" v-if="step === 1">
        <button class="sign" type="link"></button>
        <b-button
          type="submit"
          @click="nextStep"
          variant="amej-button"
          :disabled="isLoading"
          >Next</b-button
        >
      </div>
      <div class="next-button" v-if="step === 2">
        <b-button type="button" @click="prevStep" variant="outline-none">Back</b-button>
        <b-button
          type="submit"
          @click="nextStep"
          variant="amej-button"
          :disabled="isLoading"
          >Next</b-button
        >
      </div>
      <div class="next-button" v-if="step === 3">
        <b-button type="button" @click="prevStep" variant="outline-none">Back</b-button>
        <b-button
          type="submit"
          @click="submitRegister"
          variant="amej-button"
          :disabled="isLoading"
          >{{ isLoading ? "Please wait..." : "Next" }}</b-button
        >
      </div>
      <div class="next-button" v-if="step === 4 && !sessionExpired">
        <b-button type="button" @click="backToForm" variant="outline-none">Back</b-button>
        <b-button
          type="submit"
          @click="submitOtp"
          variant="amej-button"
          :disabled="isLoading"
          >{{ isLoading ? "Verifying..." : "Verify" }}</b-button
        >
      </div>
      <div class="next-button" v-if="sessionExpired">
        <b-button type="button" @click="startOver" variant="outline-none">Start again</b-button>
        <b-button
          type="submit"
          @click="restartRegistration"
          variant="amej-button"
          :disabled="isLoading"
          >{{ isLoading ? "Please wait..." : "Get a new code" }}</b-button
        >
      </div>
      <div class="next-button" v-if="step === 5">
        <b-button type="button" @click="goToLogin" variant="amej-button">Sign in</b-button>
      </div>
    </div>
    <Footer></Footer>
  </div>
</template>
<script>
import LogoDescription from "../reuseable_components/LogoDescription";
import Footer from "../reuseable_components/Footer";
import { mapActions, mapState } from "vuex";
import { readPendingRegistration, clearPendingRegistration } from "../../store/actions";
import SearchSelect from "../reuseable_components/SearchSelect.vue";
import { detectCountry, matchCountry } from "../../utils/geoip";

export default {
  name: "UserRegister",
  components: {
    LogoDescription: LogoDescription,
    Footer: Footer,
    SearchSelect: SearchSelect,
  },
  data() {
    return {
      step: 1,
      isLoading: false,
      showPassword: false,
      showConfirmPassword: false,
      detectedCountry: null,
      // Set once the IP guess has been applied, so it can never overwrite a country the
      // person has already picked (the lookup and the country list race each other).
      countryAutoFilled: false,
      otp_code: "",
      otpLength: 6,
      errors: {},
      formError: "",
      infoMessage: "",
      successText: "",
      resendCooldown: 0,
      cooldownTimer: null,
      redirectTimer: null,
      form: {
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        country: "",
        phone_number: "",
        date_of_birth: "",
        password: "",
        confirm_password: "",
      },
      valid_email: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/,
      valid_password: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,24}$/,
      valid_phone: /^[0-9]{6,15}$/,
    };
  },
  computed: {
    ...mapState([
      "successmessage",
      "errormessage",
      "message",
      "countries",
      "registerErrors",
      "registerEmail",
      "otpExpiresIn",
      "registerStatus",
    ]),
    /**
     * The API's country rows, each given a "+977" note so the code is visible in the list
     * and searchable alongside the name.
     */
    countryOptions: function () {
      const list = Array.isArray(this.countries) ? this.countries : [];
      return list.map(function (c) {
        const code = String(c.country_dialing_code || c.dialing_code || c.phone_code || "")
          .replace(/^\+/, "").trim();
        return {
          id: c.id,
          name: c.name || c.country_name || c.title || c.country || "",
          dial_label: code ? "+" + code : "",
        };
      });
    },
    countryPlaceholder: function () {
      return this.countryOptions.length ? "Country" : "Loading countries...";
    },
    selectedCountry: function () {
      const list = Array.isArray(this.countries) ? this.countries : [];
      const found = list.filter(
        function (c) { return String(c.id) === String(this.form.country); }.bind(this)
      );
      return found.length ? found[0] : null;
    },
    // phone_country_code is derived from the chosen country - never asked for
    /**
     * Digits only - your API stores dialing codes WITHOUT a leading "+"
     * (see contact_person_country_code / business_phone_country_code = "977").
     */
    phoneCountryCode: function () {
      const c = this.selectedCountry;
      if (!c) { return ""; }
      const code = c.country_dialing_code || c.dialing_code || c.phone_code || "";
      if (!code) { return ""; }
      return String(code).replace(/^\+/, "").trim();
    },
    phoneLabel: function () {
      return this.phoneCountryCode
        ? "Phone Number (+" + this.phoneCountryCode + ")"
        : "Phone Number";
    },
    // Local (not UTC) calendar date as YYYY-MM-DD
    todayIso: function () {
      const d = new Date();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return d.getFullYear() + "-" + m + "-" + day;
    },
    // 404 from verify-otp = the pending registration is gone (expired / not found).
    // Offer recovery instead of leaving the user stuck on the OTP screen.
    /**
     * The banner is suppressed when the same sentence is already printed under a
     * field. A 409 sets both `message` and `fieldErrors.email` to "A user with this
     * email already exists", which is why it appeared twice on one screen.
     */
    showFormError: function () {
      if (!this.formError) { return false; }
      return this.visibleFieldErrors().indexOf(String(this.formError)) === -1;
    },
    sessionExpired: function () {
      return this.step === 4 && this.registerStatus === 404;
    },
    stepSubtitle: function () {
      if (this.step === 1) { return "Add your Mobile Number"; }
      if (this.step === 2) { return "Setup some Basic Detail"; }
      if (this.step === 3) { return "Setup your Login Detail"; }
      if (this.step === 4) { return "Confirm you got our email"; }
      return "Account created";
    },
    maxDob: function () {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return d.getFullYear() + "-" + m + "-" + day;
    },
  },
  created() {
    const vm = this;
    this.listCountries().then(function () { vm.applyDetectedCountry(); });
    // Fire-and-forget: if this never answers, the field simply stays empty.
    detectCountry().then(function (found) {
      vm.detectedCountry = found;
      vm.applyDetectedCountry();
    });
    // auth/register has already run if a pending registration is parked. Returning the
    // user to a blank step 1 after a refresh would strand them: the code is in their
    // inbox but the form that accepts it is gone. Put them back on the OTP step.
    const pending = readPendingRegistration();
    if (pending && pending.email) {
      this.form.email = pending.email;
      this.$store.commit("setRegisterEmail", pending.email);
      this.step = 4;
      this.infoMessage =
        "We already sent a code to " + pending.email + ". Enter it below, or resend it.";
    }
  },
  beforeDestroy() {
    if (this.cooldownTimer) { clearInterval(this.cooldownTimer); }
    if (this.redirectTimer) { clearTimeout(this.redirectTimer); }
  },
  methods: {
    ...mapActions(["listCountries", "userRegister", "userVerifyOtp", "userResendOtp"]),

    /**
     * Pre-selects the country the visitor is browsing from.
     *
     * Called from both the country-list response and the IP lookup, because either can
     * land first. It only ever fills an EMPTY field once - a guess must never overwrite
     * a deliberate choice, and must not re-apply if the person clears the field.
     */
    applyDetectedCountry() {
      if (this.countryAutoFilled || this.form.country) { return; }
      if (!this.detectedCountry || !this.countries || !this.countries.length) { return; }
      const match = matchCountry(this.countries, this.detectedCountry);
      if (!match) { return; }
      this.form.country = match.id;
      this.countryAutoFilled = true;
    },

    // local validation first, then any field error returned by the server
    fieldError(name) {
      if (this.errors[name]) { return this.errors[name]; }
      const serverErrors = this.registerErrors || {};
      return serverErrors[name] || "";
    },

    /** Every field error currently on screen, so the banner can avoid repeating one. */
    visibleFieldErrors() {
      const out = [];
      const merged = Object.assign({}, this.registerErrors || {}, this.errors || {});
      Object.keys(merged).forEach(function (k) {
        if (merged[k]) { out.push(String(merged[k])); }
      });
      return out;
    },

    clearMessages() {
      this.errors = {};
      this.formError = "";
      this.infoMessage = "";
    },

    validateStep(step) {
      const f = this.form;
      const e = {};
      if (step === 1) {
        if (!f.country) { e.country = "Country required."; }
        if (f.phone_number && !this.valid_phone.test(f.phone_number)) {
          e.phone_number = "Enter digits only (6-15), without the country code.";
        }
      }
      if (step === 2) {
        if (!f.first_name) { e.first_name = "First name is required."; }
        if (!f.last_name) { e.last_name = "Last name is required."; }
        if (f.date_of_birth) {
          const dob = new Date(f.date_of_birth);
          if (isNaN(dob.getTime())) {
            e.date_of_birth = "Please enter a valid date.";
          } else if (f.date_of_birth >= this.todayIso) {
            // Compare YYYY-MM-DD strings so the local/UTC midnight offset
            // cannot let today's date slip through as "in the past".
            e.date_of_birth = "Date of birth must be in the past.";
          }
        }
      }
      if (step === 3) {
        if (!f.email) {
          e.email = "Email address is required.";
        } else if (!this.valid_email.test(f.email)) {
          e.email = "Please enter a valid email address.";
        }
        if (!f.password) {
          e.password = "Password is required.";
        } else if (!this.valid_password.test(f.password)) {
          e.password = "Password must be 8-24 characters with an uppercase, lowercase, number and special character.";
        }
        if (!f.confirm_password) {
          e.confirm_password = "Please confirm your password.";
        } else if (f.confirm_password !== f.password) {
          e.confirm_password = "Passwords do not match.";
        }
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    nextStep() {
      this.clearMessages();
      if (!this.validateStep(this.step)) {
        this.formError = "Please correct the highlighted fields.";
        return;
      }
      this.step = this.step + 1;
      window.scrollTo(0, 0);
    },

    prevStep() {
      this.clearMessages();
      if (this.step > 1) { this.step = this.step - 1; }
      window.scrollTo(0, 0);
    },

    /**
     * Mirrors the auth/register schema exactly. Every key is sent (empty string when the
     * user left it blank) rather than being omitted - the previous version dropped
     * middle_name / phone_number / country entirely, which the backend rejected.
     * date_of_birth is the one exception: it must be a real date or absent.
     */
    buildPayload() {
      const f = this.form;
      const clean = function (v) { return v === null || v === undefined ? "" : String(v).trim(); };
      const payload = {
        email: clean(f.email),
        password: f.password,
        confirm_password: f.confirm_password,
        first_name: clean(f.first_name),
        middle_name: clean(f.middle_name),
        last_name: clean(f.last_name),
        phone_number: clean(f.phone_number),
        phone_country_code: this.phoneCountryCode,
        country: this.selectedCountry ? this.selectedCountry.name : "",
        default_currency: "USD",
      };
      if (f.date_of_birth) { payload.date_of_birth = f.date_of_birth; }
      return payload;
    },

    /** STEP 1 -> POST auth/register (sends the OTP) */
    submitRegister() {
      this.clearMessages();
      this.$store.commit("setRegisterErrors", {});
      for (let s = 1; s <= 3; s++) {
        if (!this.validateStep(s)) {
          this.step = s;
          this.formError = "Please correct the highlighted fields.";
          window.scrollTo(0, 0);
          return;
        }
      }
      this.isLoading = true;
      const vm = this;
      return this.userRegister(this.buildPayload())
        .then(function (res) {
          // Driven by the action's own result, not the shared successmessage flag.
          if (res && res.ok) {
            vm.step = 4;
            vm.otp_code = "";
            vm.infoMessage = res.message || "Verification code sent to your email.";
            vm.startCooldown();
            window.scrollTo(0, 0);
          } else {
            vm.formError = (res && res.message) || "Unable to register. Please try again.";
            vm.focusFirstErrorStep(res && res.fieldErrors);
          }
        })
        .catch(function () {
          vm.formError = "Unable to register. Please try again.";
        })
        .then(function () {
          vm.isLoading = false;
        });
    },

    /** STEP 2 -> POST auth/verify-otp (creates the account) */
    submitOtp() {
      this.clearMessages();
      if (!this.otp_code) {
        this.errors = { otp_code: "Verification code is required." };
        return;
      }
      if (this.otp_code.length !== this.otpLength) {
        this.errors = { otp_code: "Please enter the " + this.otpLength + "-digit code." };
        return;
      }
      this.isLoading = true;
      const vm = this;
      return this.userVerifyOtp({
        email: this.registerEmail || this.form.email,
        otp_code: String(this.otp_code).trim(),
      })
        .then(function (res) {
          if (res && res.ok) {
            vm.successText = res.message || "Account created successfully!";
            vm.step = 5;
            vm.stopCooldown();
            window.scrollTo(0, 0);
            vm.redirectTimer = setTimeout(function () {
              vm.goToLogin();
            }, 3000);
          } else {
            vm.formError = (res && res.message) || "Invalid or expired verification code.";
          }
        })
        .catch(function () {
          vm.formError = "Unable to verify the code. Please try again.";
        })
        .then(function () {
          vm.isLoading = false;
        });
    },

    /** STEP 3 -> POST auth/resend-otp (server enforces a 2 minute cooldown) */
    resendCode() {
      if (this.resendCooldown > 0) { return; }
      this.clearMessages();
      this.isLoading = true;
      const vm = this;
      return this.userResendOtp({ email: this.registerEmail || this.form.email })
        .then(function (res) {
          if (res && res.ok) {
            vm.infoMessage = res.message || "New verification code sent to your email.";
          } else {
            vm.formError = (res && res.message) || "Unable to resend the code.";
          }
          // either way the server cooldown is now running
          vm.startCooldown();
        })
        .catch(function () {
          vm.formError = "Unable to resend the code. Please try again.";
        })
        .then(function () {
          vm.isLoading = false;
        });
    },

    startCooldown() {
      this.stopCooldown();
      this.resendCooldown = 120; // matches the API's 2 minute cooldown
      const vm = this;
      this.cooldownTimer = setInterval(function () {
        vm.resendCooldown = vm.resendCooldown - 1;
        if (vm.resendCooldown <= 0) { vm.stopCooldown(); }
      }, 1000);
    },

    stopCooldown() {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer);
        this.cooldownTimer = null;
      }
      this.resendCooldown = 0;
    },

    /** Re-runs auth/register to create a fresh pending registration + OTP. */
    restartRegistration() {
      this.clearMessages();
      this.$store.commit("setRegisterStatus", null);
      this.stopCooldown();
      const vm = this;
      this.isLoading = true;
      return this.userRegister(this.buildPayload())
        .then(function (res) {
          if (res && res.ok) {
            vm.otp_code = "";
            vm.infoMessage = res.message || "A new verification code has been sent to your email.";
            vm.startCooldown();
          } else {
            vm.formError = (res && res.message) || "Unable to send a new code. Please start again.";
          }
        })
        .catch(function () {
          vm.formError = "Unable to send a new code. Please start again.";
        })
        .then(function () {
          vm.isLoading = false;
        });
    },

    /** Back to step 1 with the typed details still in place. */
    startOver() {
      this.clearMessages();
      this.$store.commit("setRegisterStatus", null);
      this.stopCooldown();
      this.otp_code = "";
      // Drop the parked registration, otherwise created() would bounce the user
      // straight back to the OTP step on the next mount.
      clearPendingRegistration();
      this.$store.commit("setRegisterEmail", "");
      this.step = 1;
      window.scrollTo(0, 0);
    },

    /** Server rejected a field - jump back to the step that owns it. */
    focusFirstErrorStep(fieldErrors) {
      if (!fieldErrors) { return; }
      const stepOf = {
        country: 1, phone_number: 1, phone_country_code: 1,
        first_name: 2, middle_name: 2, last_name: 2, date_of_birth: 2,
        email: 3, password: 3, confirm_password: 3,
      };
      const keys = Object.keys(fieldErrors);
      for (let i = 0; i < keys.length; i++) {
        const s = stepOf[keys[i]];
        if (s) { this.step = s; window.scrollTo(0, 0); return; }
      }
    },

    backToForm() {
      this.clearMessages();
      this.$store.commit("setRegisterStatus", null);
      this.step = 3;
      window.scrollTo(0, 0);
    },

    goToLogin() {
      if (this.redirectTimer) { clearTimeout(this.redirectTimer); }
      this.$router.replace({ name: "login" });
    },
  },
  mounted() {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 200);
  },
};
</script>
<style lang="scss" scoped>
@import "../../css/style.scss";
</style>
